import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Tooltip,
  Fade
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  Download,
  RocketLaunch,
  AttachMoney,
  Speed,
  WorkspacePremium
} from '@mui/icons-material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import html2canvas from 'html2canvas';

// Color palette
const colors = {
  background: '#0B0C10',
  cardBg: 'rgba(255, 255, 255, 0.03)',
  cardBorder: 'rgba(255, 255, 255, 0.08)',
  cyan: '#00FFF5',
  purple: '#9D00FF',
  green: '#00E676',
  amber: '#FFB300',
  red: '#FF5252',
  white: '#FFFFFF',
  muted: '#8B8B8B'
};

// Get color based on viability score
function getViabilityColor(score) {
  if (score >= 70) return colors.green;
  if (score >= 40) return colors.amber;
  return colors.red;
}

// Get color based on decay risk
function getDecayColor(label) {
  if (label === 'Low') return colors.green;
  if (label === 'Moderate') return colors.amber;
  return colors.red;
}

// Get color based on income delta
function getIncomeColor(delta) {
  if (delta >= 20000) return colors.green;
  if (delta >= 0) return colors.amber;
  return colors.red;
}

// Get color based on path weeks
function getPathColor(weeks) {
  if (weeks <= 12) return colors.green;
  if (weeks <= 24) return colors.amber;
  return colors.red;
}

// Format currency
function formatCurrency(amount) {
  if (amount >= 1000) {
    return `$${Math.round(amount / 1000)}K`;
  }
  return `$${amount.toLocaleString()}`;
}

// Circular progress ring component
function ViabilityRing({ score, size = 180 }) {
  const strokeWidth = 12;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const progress = (score / 100) * circumference;
  const color = getViabilityColor(score);

  return (
    <Box sx={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255, 255, 255, 0.1)"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={circumference - progress}
          strokeLinecap="round"
          style={{
            transition: 'stroke-dashoffset 1s ease-out',
            filter: `drop-shadow(0 0 8px ${color})`
          }}
        />
      </svg>
      {/* Score text */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center'
        }}
      >
        <Typography
          sx={{
            fontSize: 48,
            fontWeight: 700,
            color: colors.white,
            lineHeight: 1,
            fontFamily: '"JetBrains Mono", monospace'
          }}
        >
          {score}
        </Typography>
        <Typography
          sx={{
            fontSize: 16,
            color: colors.muted,
            fontFamily: '"JetBrains Mono", monospace'
          }}
        >
          / 100
        </Typography>
      </Box>
    </Box>
  );
}

// Metric card component
function MetricCard({ icon, title, value, subtitle, color, tooltip }) {
  return (
    <Tooltip title={tooltip || ''} arrow placement="top">
      <Paper
        sx={{
          p: 2.5,
          background: colors.cardBg,
          border: `1px solid ${colors.cardBorder}`,
          borderRadius: 2,
          textAlign: 'center',
          cursor: 'default',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: color,
            background: `${color}10`
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
          {icon}
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: colors.muted,
              textTransform: 'uppercase',
              letterSpacing: 1
            }}
          >
            {title}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: 28,
            fontWeight: 700,
            color: color,
            fontFamily: '"JetBrains Mono", monospace',
            lineHeight: 1.2
          }}
        >
          {value}
        </Typography>
        {subtitle && (
          <Typography
            sx={{
              fontSize: 12,
              color: colors.muted,
              mt: 0.5
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Paper>
    </Tooltip>
  );
}

export default function MarketVerdictCard({ formData, onNext, onBack }) {
  const [loading, setLoading] = useState(true);
  const [verdict, setVerdict] = useState(null);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const cardRef = useRef(null);

  // Extract data from formData
  const currentRole = formData?.resumeData?.currentRole || formData?.currentRole || 'Unknown';
  const targetRole = formData?.selectedSuggestion?.title || formData?.targetRole || 'Unknown';
  const profile = {
    skills: formData?.resumeData?.skills || formData?.skills || [],
    certifications: formData?.resumeData?.certifications || formData?.certifications || [],
    yearsExperience: formData?.resumeData?.yearsExperience || formData?.yearsExperience || 0
  };

  // Fetch market verdict on mount
  useEffect(() => {
    async function fetchVerdict() {
      try {
        setLoading(true);
        setError(null);

        const functions = getFunctions();
        const calculateMarketVerdictFn = httpsCallable(functions, 'calculateMarketVerdict');
        
        const result = await calculateMarketVerdictFn({
          profile,
          currentRole,
          targetRole
        });

        if (result.data?.success) {
          setVerdict(result.data.verdict);
        } else {
          throw new Error('Failed to calculate market verdict');
        }
      } catch (err) {
        console.error('Error fetching verdict:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchVerdict();
  }, [currentRole, targetRole]);

  // Download as PNG
  const handleDownload = async () => {
    if (!cardRef.current) return;
    
    setDownloading(true);
    try {
      // Hide buttons for screenshot
      const buttons = cardRef.current.querySelector('.action-buttons');
      if (buttons) buttons.style.display = 'none';

      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: colors.background,
        scale: 2,
        logging: false,
        useCORS: true
      });

      // Restore buttons
      if (buttons) buttons.style.display = 'flex';

      // Create download link
      const link = document.createElement('a');
      link.download = `market-verdict-${targetRole.replace(/\s+/g, '-').toLowerCase()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Error downloading:', err);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 400,
          gap: 3
        }}
      >
        <CircularProgress sx={{ color: colors.cyan }} size={60} />
        <Typography sx={{ color: colors.muted, fontSize: 16 }}>
          Analyzing market data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 400,
          gap: 2
        }}
      >
        <Warning sx={{ fontSize: 48, color: colors.red }} />
        <Typography sx={{ color: colors.white, fontSize: 18 }}>
          Unable to calculate market verdict
        </Typography>
        <Typography sx={{ color: colors.muted, fontSize: 14, textAlign: 'center', maxWidth: 400 }}>
          {error}
        </Typography>
        <Button
          variant="outlined"
          onClick={onBack}
          sx={{ mt: 2, borderColor: colors.muted, color: colors.muted }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  const viabilityColor = getViabilityColor(verdict.viabilityScore);
  const incomeColor = getIncomeColor(verdict.incomeDelta);
  const decayColor = getDecayColor(verdict.decayRiskLabel);
  const pathColor = getPathColor(verdict.fastestPathWeeks);

  return (
    <Fade in={true} timeout={500}>
      <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto' }}>
        {/* The shareable card */}
        <Paper
          ref={cardRef}
          sx={{
            background: `linear-gradient(180deg, ${colors.background} 0%, #1a1a2e 100%)`,
            borderRadius: 3,
            p: 4,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Background decoration */}
          <Box
            sx={{
              position: 'absolute',
              top: -100,
              right: -100,
              width: 300,
              height: 300,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${colors.purple}20 0%, transparent 70%)`,
              pointerEvents: 'none'
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -50,
              left: -50,
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${colors.cyan}15 0%, transparent 70%)`,
              pointerEvents: 'none'
            }}
          />

          {/* Title */}
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: 14,
              fontWeight: 600,
              color: colors.cyan,
              textTransform: 'uppercase',
              letterSpacing: 3,
              mb: 1
            }}
          >
            Your Market Verdict
          </Typography>

          {/* Target role */}
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: 11,
              color: colors.muted,
              mb: 4
            }}
          >
            Transition to <strong style={{ color: colors.white }}>{targetRole}</strong>
          </Typography>

          {/* Main viability score */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
            <ViabilityRing score={verdict.viabilityScore} />
            <Typography
              sx={{
                mt: 2,
                fontSize: 11,
                fontWeight: 600,
                color: colors.muted,
                textTransform: 'uppercase',
                letterSpacing: 1
              }}
            >
              Market Viability Score
            </Typography>
            <Typography
              sx={{
                mt: 1,
                fontSize: 13,
                color: viabilityColor,
                textAlign: 'center',
                maxWidth: 300,
                fontStyle: 'italic'
              }}
            >
              "{verdict.viabilityLabel}"
            </Typography>
          </Box>

          {/* Metric grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 2,
              mb: 4
            }}
          >
            <MetricCard
              icon={<AttachMoney sx={{ fontSize: 16, color: incomeColor }} />}
              title="Income Delta"
              value={verdict.incomeDelta >= 0 ? `+${formatCurrency(verdict.incomeDelta)}` : formatCurrency(verdict.incomeDelta)}
              subtitle="per year"
              color={incomeColor}
              tooltip={`Current: ${formatCurrency(verdict.currentSalary)} → Target: ${formatCurrency(verdict.targetSalary)}`}
            />
            <MetricCard
              icon={verdict.decayRiskLabel === 'Low' ? <CheckCircle sx={{ fontSize: 16, color: decayColor }} /> : <Warning sx={{ fontSize: 16, color: decayColor }} />}
              title="Role Decay Risk"
              value={verdict.decayRiskLabel.toUpperCase()}
              subtitle={verdict.decayRiskTrend || 'Stable'}
              color={decayColor}
              tooltip="Risk your current role becomes obsolete due to automation or market decline"
            />
            <MetricCard
              icon={<Speed sx={{ fontSize: 16, color: pathColor }} />}
              title="Fastest Path"
              value={verdict.fastestPathWeeks >= 52 ? `${Math.round(verdict.fastestPathWeeks / 52)} years` : `${verdict.fastestPathWeeks} weeks`}
              subtitle={verdict.requiresDegree ? verdict.targetEducation : "with guidance"}
              color={pathColor}
              tooltip={verdict.requiresDegree 
                ? `Requires ${verdict.targetEducation} - includes education timeline` 
                : "Estimated time to become market-eligible with focused effort"}
            />
            <MetricCard
              icon={<WorkspacePremium sx={{ fontSize: 16, color: colors.cyan }} />}
              title="Top Landing Role"
              value="→"
              subtitle={targetRole}
              color={colors.cyan}
              tooltip="Best-fit target role based on your profile"
            />
          </Box>

          {/* Data source attribution */}
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: 10,
              color: colors.muted,
              mb: 3
            }}
          >
            Data: Bureau of Labor Statistics 2024 • neurolnet.ai
          </Typography>

          {/* Action buttons - hidden in PNG export */}
          <Box
            className="action-buttons"
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center'
            }}
          >
            <Button
              variant="outlined"
              startIcon={downloading ? <CircularProgress size={16} sx={{ color: colors.muted }} /> : <Download />}
              onClick={handleDownload}
              disabled={downloading}
              sx={{
                borderColor: colors.muted,
                color: colors.muted,
                px: 3,
                '&:hover': {
                  borderColor: colors.white,
                  color: colors.white
                }
              }}
            >
              {downloading ? 'Saving...' : 'Save to Photos'}
            </Button>
            <Button
              variant="contained"
              startIcon={<RocketLaunch />}
              onClick={onNext}
              sx={{
                background: `linear-gradient(135deg, ${colors.cyan} 0%, ${colors.purple} 100%)`,
                color: colors.background,
                fontWeight: 600,
                px: 3,
                '&:hover': {
                  background: `linear-gradient(135deg, ${colors.cyan}dd 0%, ${colors.purple}dd 100%)`
                }
              }}
            >
              Unlock Full Plan
            </Button>
          </Box>
        </Paper>

        {/* Back button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            onClick={onBack}
            sx={{
              color: colors.muted,
              '&:hover': { color: colors.white }
            }}
          >
            ← Back to role selection
          </Button>
        </Box>
      </Box>
    </Fade>
  );
}

